import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserRequest } from './RequestObject/UserRequest';
import { AppUserHomeRequest } from './RequestObject/AppUserHomeRequest';
import { AlarmLogRequest } from './RequestObject/AlarmLogRequest';
import { OcLogRequest } from './RequestObject/OcLogRequest';
import { tap, take, shareReplay, timeout } from 'rxjs/operators';
import { StorageService } from '../commons/storage.service';
import { GlobalVariablesService } from '../global-variables.service';
import { UserLoginRequest } from './RequestObject/UserLoginRequest';
import { PreAuthTokenRequest } from './RequestObject/PreAuthTokenRequest';
import { CodewordChangeRequest } from './RequestObject/CodewordChangeRequest';
import { IncidencesRequest } from './RequestObject/IncidencesRequest';
import { AppUserTextRequest } from './RequestObject/AppUserTextRequest';
import { NotificationGeoAroundRequest } from './RequestObject/NotificationGeoAroundRequest';
import { DeviceUpdRequest } from './RequestObject/DeviceUpdRequest';
import { AppUserScheduleRequest } from './RequestObject/AppuserScheduleRequest';
import { DocsListRequest } from './RequestObject/DocsListRequest';
import { InvoicesRequest } from './RequestObject/InvoicesRequest';
import { AppUserInvoiceDwl } from './RequestObject/AppUserInvoiceDwl';
import { AppChatInsRequest } from './RequestObject/AppChatInsRequest';
import { AppChatMsgGetRequest } from './RequestObject/AppChatMsgGetRequest';
import { GappUserSettingsRequest } from './RequestObject/GappUserSettingsRequest';
import { CappUserSettingsRequest } from './RequestObject/CappUserSettingsRequest';
import { CamerasListRequest } from './RequestObject/CamerasListRequest';
import { CommandConnectorQueueInsRequest } from './RequestObject/CommandConnectorQueueInsRequest';
import { UserListRequest } from './RequestObject/UserListRequest';
import { UserUpdRequest } from './RequestObject/UserUpdRequest';
import { UserQrRequest } from './RequestObject/UserQrRequest';
import { AppUserDeviceConfiguredRequest } from './RequestObject/AppUserDeviceConfiguredRequest';
import { UserLogin } from '../../models/UserLogin';
import { AppuserUserAccessValidateCodeGenRequest } from './RequestObject/AppuserUserAccessValidateCodeGenRequest';
import { AppuserUserAccessValidateCodeProcRequest } from './RequestObject/AppuserUserAccessValidateCodeProcRequest';
import { VersionControlRequest } from './RequestObject/VersionControlRequest';
import { LogOutRequest } from './RequestObject/LogOutRequest';
import { AppUserDeviceListRequest } from './RequestObject/AppUserDeviceListRequest';
import { CheckDealerRequest } from './RequestObject/CheckDealerRequest';
import { ContactChangeRequest } from './RequestObject/ContactChangeRequest';
import { AppuserZoneUpdRequest } from './RequestObject/AppuserZoneUpdRequest';
import { InstallationNameChangeRequest } from './RequestObject/InstallationNameChangeRequest';
import { ImageNotificationRequest } from './RequestObject/ImageNotificationRequest';
import { AppUserSvrqDownload } from './RequestObject/AppUserSvrqDownload';
import { CameraSettingsRequest } from './RequestObject/CameraSettingsRequest';
import { LastInsRequest } from './RequestObject/LastInsRequest';
import { AppUserCardNewRequest } from './RequestObject/AppUserCardNewRequest';
import { AppUserNpsInsRequest } from './RequestObject/AppUserNpsInsRequest';
import { RegisterRequest } from './RequestObject/RegisterRequest';
import { DeviceLogRequest } from './RequestObject/DeviceLogRequest';


@Injectable({
  providedIn: 'root'
})
export class SicorApiService {

  constructor(private http: HttpClient,
    private storage: StorageService,
    public gvs: GlobalVariablesService) { }

  public async getToken(renew: boolean = false) {
    let token = await this.storage.getItem(this.gvs.TOKEN_KEY);
    let user = await this.storage.getObject(this.gvs.AUTH_KEY);
    user = user != null && user.type === 'EMAIL' ? new UserLoginRequest(user.userName, user.password) : new UserLoginRequest('', '', 'PREAUTH');    /* --- Comprobar si auth por defecto o usuario registrado para obtener un token u otro. ---*/
    /* --- Comprobar si auth por defecto o usuario registrado para obtener un token u otro. ---*/
    let validUntil: Date = await this.storage.getObject(user.type === 'EMAIL' ? this.gvs.TOKEN_EXPIRATION_KEY : this.gvs.TOKEN_EXPIRATION_KEY_PREAUTH);
    if (renew || !token || !validUntil || new Date(validUntil) < new Date()) {
      token = await this.getAuthToken(user, token, validUntil);
    }
    return token;
  }

  public async getAuthToken(user: UserLoginRequest, token: string, validUntil: Date): Promise<string>{
    return new Promise(async (resolve, reject) => {
      await (await this.userLogAccess(user)).pipe(
        take(1),
        tap(
          async res => {
            if (res.result) {
              token = res.result;
              await this.storage.setItem(this.gvs.TOKEN_KEY, token);
              if (res.expiration_seconds) {
                validUntil = new Date();
                /* --- El tiempo de caducidad del token en auth por defecto es siempre de 5 minutos. ---*/
                user.type === 'EMAIL' ? validUntil.setSeconds(res.expiration_seconds) : validUntil.setSeconds(300);
                await this.storage.setObject(user.type === 'EMAIL' ? this.gvs.TOKEN_EXPIRATION_KEY : this.gvs.TOKEN_EXPIRATION_KEY_PREAUTH, validUntil);
              }
              resolve(token);
            } else {
              resolve(null); 
            }
          }, e => {
            resolve(null);
          }
        )).toPromise();
    });
  }

  public userLogAccess(data): Observable<any> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/auth`;
    return this.http.post<any>(url, data).pipe(timeout(15000));
  }

  public async requestNewPassSms(email: string, token: string): Promise<Observable<any>> {
    const user = { user: email, app_name: 'SIC' };
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appusers_newpass_sms`;
    const httpHeaders = new HttpHeaders({
      'User-Token': token,
    });
    return this.http.post<any>(url, user, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestPasswordUpdate(updateCredentials, token: string): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appusers_newpass_upd`;
    const httpHeaders = new HttpHeaders({
      'User-Token': token,
    });
    return this.http.post<any>(url, updateCredentials, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async listInstallation(userRequest: UserRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_installation_list`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, userRequest, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppUserHome(userRequest: AppUserHomeRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_home`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, userRequest, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestInstallationInfo(requestParams: AppUserHomeRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_ma_installations`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestContactUsers(userProfileRequest: AppUserHomeRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_contact_users`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, userProfileRequest, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestContactUsersChange(contactChangeRequest: ContactChangeRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_contact_user`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, contactChangeRequest, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestAlarmsLog(requestParams: AlarmLogRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_alarmlog`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestOpenCloseLog(requestParams: OcLogRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_oclog`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestDevicesLog(requestParams: DeviceLogRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_devices_log`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  async api(url: string, requestParams: any, httpHeaders: any) {
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestIncidenceTypes(requestParams): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_sv_problems`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async sendIncidence(requestParams): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_sv_request`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestInstallationCodewords(requestParams: AppUserHomeRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_ins_codewords`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestInstallationCodewordsChange(requestParams: CodewordChangeRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appusers_ins_codewords`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestIncidences(requestParams: IncidencesRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_sv_request`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestEmergencyTypes(requestParams): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_menu_preferences`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async sendEmergency(requestParams): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_alarm_send`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async gAppuserTextRequest(requestParams: AppUserTextRequest) {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_text`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async sendNotificationGeoAroundRequest(requestParams: NotificationGeoAroundRequest) {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_geocerca_in_out`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async sendCyberSafeguardSignal(requestParams): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_alarm_send_std`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async sendDeviceRequest(requestParams: DeviceUpdRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appusers_id_device_upd`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserCommandConnectorQueueInsRequest(requestParams: CommandConnectorQueueInsRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_command_connector_queue_ins`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async appUserScheduleRequest(requestParams: AppUserScheduleRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_schedule`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async isAppUserDocsListRequest(requestParams: DocsListRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_docs_list`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async isAppuserInvoicesRequest(requestParams: InvoicesRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_invoices`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async gAppuserInvoiceDwlRequest(requestParams: AppUserInvoiceDwl): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_invoice_dwl`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async appGetChatRequest(requestParams: AppChatMsgGetRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_app_chat_msg_get`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async appInsChatRequest(requestParams: AppChatInsRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_app_chat_ins`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async gAppuserSettingsRequest(requestParams: GappUserSettingsRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_settings`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserSettingsRequest(requestParams: CappUserSettingsRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_settings`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async camerasListRequest(requestParams: CamerasListRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_cameras_list`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async downloadPhotoRequest(requestParams: CamerasListRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_image_frm_log`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async listUsersRequest(requestParams: UserListRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_user_list`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserUserUpdRequest(requestParams: UserUpdRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_user_upd`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserUserQrRequest(requestParams: UserQrRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_user_qr`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async vAppuserStartRegistrationRequest(requestParams: RegisterRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/v_appuser_startregistration `;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserUserAccessValidateCodeGen(requestParams): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_validate_code_gen`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async validateCodeProc(requestParams: AppuserUserAccessValidateCodeProcRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_validate_code_proc`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async gAppuserDeviceConfiguredRequest(requestParams: AppUserDeviceConfiguredRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_device_configured`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async checkVersion(requestParams: VersionControlRequest, token: string): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/checkVersion`;
    const httpHeaders = new HttpHeaders({
      'User-Token': token,
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async logOut(requestParams: LogOutRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_lo`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async requestBillingInfo(requestParams: AppUserHomeRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_billing_accounts`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async isAppuserDeviceListRequest(requestParams: AppUserDeviceListRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_device_list`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

public async cAppuserZoneUpdRequest(requestParams: AppuserZoneUpdRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_zone_upd`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }
  
  public async checkDealerRequest(requestParams: CheckDealerRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/check_dealer`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async gAppuserViewInvoiceRequest(requestParams: AppUserInvoiceDwl): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_invoice_view`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserAliasNameUpd(requestParams: InstallationNameChangeRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_alias_name_upd`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }
  
  public async getImageNotificationRequest(requestParams: ImageNotificationRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/is_appuser_notification_image`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async getCameraSettings(requestParams: CameraSettingsRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_camera_settings`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }
  
  public async gAppuserSvrqViewRequest(requestParams: AppUserSvrqDownload): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_svrq_view`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserLastIns(userRequest: LastInsRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_last_ins`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, userRequest, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserResendCode(requestParams: AppuserUserAccessValidateCodeGenRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_resend_code`;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async gAppUserCardNewsRequest(requestParams: AppUserCardNewRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/g_appuser_card_news `;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }

  public async cAppuserNpsInsRequest(requestParams: AppUserNpsInsRequest): Promise<Observable<any>> {
    const url = `${environment.URL_BASE_APP_USUARIOS}/api/v1/c_appuser_nps_ins `;
    const httpHeaders = new HttpHeaders({
      'User-Token': await this.getToken(),
    });
    return this.http.post<any>(url, requestParams, { headers: httpHeaders }).pipe(timeout(15000));
  }
  
}